import Button from "devextreme-react/button";
import CheckBox from "devextreme-react/check-box";
import TagBox from "devextreme-react/tag-box";
import React, { useState, useEffect, useMemo } from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../dtos/DefaultStepDto";
import { WorkflowConfigOptionsDto } from "../../../dtos/WorkflowConfigOptionsDto";
import { UserRole } from "../../../dtos/UserRole";
import { NotificationTrigger } from "../../../dtos/NotificationTrigger";
import { NotificationTriggerConfig } from "../../../dtos/NotificationTriggerConfig";
import { NotificationRules } from "../../../dtos/NotificationRules";
import { StatusTransitionConfig } from "../../../dtos/StatusTransitionConfig";
import { PhaseStatus } from "../../../dtos/PhaseStatus";
import { StepStatus } from "../../../dtos/StepStatus";
import { PhaseWorkflowType } from "../../../dtos/PhaseWorkflowType";
import { ResetEmailTemplateDto } from "../../../dtos/ResetEmailTemplateDto";
import EmailTemplateViewerDialog from "./EmailTemplateViewerDialog";
import DefaultsAddedNotice, { DefaultTag } from "./components/DefaultsAddedNotice";
import { groupRoleOptions } from "../../shared/tasklist/config/audienceConfig";

// Mode determines what notifications we're editing:
// - "task": Task's own notifications (task.settings.notifications)
// - "subtask": Subtask's own notifications (subtask.settings.notifications)
// - "defaultSubtask": Task's default subtask notifications (task.settings.defaultStepSettings.notifications)
export type NotificationsMode = "task" | "subtask" | "defaultSubtask";

type Props = {
    task?: DefaultPhaseDto | null;
    subtask?: DefaultStepDto | null;
    workflowType: PhaseWorkflowType;
    configOptions: WorkflowConfigOptionsDto;
    onSave: (data: DefaultPhaseDto | DefaultStepDto) => Promise<void>;
    mode?: NotificationsMode;
};

// Helper to get trigger label
function getTriggerLabel(trigger: NotificationTrigger): string {
    switch (trigger) {
        case NotificationTrigger.OnUpload:
            return "On File Upload";
        case NotificationTrigger.OnStatusChange:
            return "On Status Change";
        case NotificationTrigger.OnComment:
            return "On Comment";
        default:
            return `Trigger ${trigger}`;
    }
}

// Helper to get trigger description
function getTriggerDescription(trigger: NotificationTrigger): string {
    switch (trigger) {
        case NotificationTrigger.OnUpload:
            return "Send notifications when files are uploaded";
        case NotificationTrigger.OnStatusChange:
            return "Send notifications when status changes";
        case NotificationTrigger.OnComment:
            return "Send notifications when comments are added";
        default:
            return "";
    }
}

// Get phase status label
function getPhaseStatusLabel(status: PhaseStatus): string {
    switch (status) {
        case PhaseStatus.NOT_STARTED: return "Not Started";
        case PhaseStatus.SETUP: return "In Progress";
        case PhaseStatus.READY: return "Ready";
        case PhaseStatus.GRANTEE_PENDING: return "Grantee Pending";
        case PhaseStatus.GRANTEE_PENDING_REVISION: return "Grantee Pending Revision";
        case PhaseStatus.PENDING: return "Pending";
        case PhaseStatus.APPROVED: return "Complete";
        case PhaseStatus.INACTIVE: return "Inactive";
        default: return `Status ${status}`;
    }
}

// Get step status label
function getStepStatusLabel(status: StepStatus): string {
    switch (status) {
        case StepStatus.NOT_STARTED: return "Not Started";
        case StepStatus.SETUP: return "In Progress";
        case StepStatus.READY: return "Ready";
        case StepStatus.GRANTEE_PENDING: return "Grantee Pending";
        case StepStatus.GRANTEE_PENDING_REVISION: return "Grantee Pending Revision";
        case StepStatus.GRANTEE_COMPLETE: return "Grantee Complete";
        case StepStatus.PENDING: return "Pending";
        case StepStatus.PENDING_REVISION: return "Pending Revision";
        case StepStatus.APPROVED: return "Complete";
        case StepStatus.INACTIVE: return "Inactive";
        default: return `Status ${status}`;
    }
}

// Phase workflow task transitions - each maps to a task-level action
const phaseTransitions: Array<{ from: PhaseStatus; to: PhaseStatus; label: string; action: string }> = [
    { from: PhaseStatus.READY, to: PhaseStatus.GRANTEE_PENDING, label: "Ready → Grantee Pending", action: "Release" },
    { from: PhaseStatus.GRANTEE_PENDING, to: PhaseStatus.PENDING, label: "Grantee Pending → Pending", action: "Submit" },
    { from: PhaseStatus.GRANTEE_PENDING_REVISION, to: PhaseStatus.PENDING, label: "Grantee Pending Revision → Pending", action: "Submit" },
    { from: PhaseStatus.PENDING, to: PhaseStatus.GRANTEE_PENDING_REVISION, label: "Pending → Grantee Pending Revision", action: "Request Revisions" },
    { from: PhaseStatus.PENDING, to: PhaseStatus.APPROVED, label: "Pending → Complete", action: "Approve" },
];

// Phase workflow subtask transitions - each maps to a task-level action
const stepTransitions: Array<{ from: StepStatus; to: StepStatus; label: string; action: string }> = [
    { from: StepStatus.READY, to: StepStatus.GRANTEE_PENDING, label: "Ready → Grantee Pending", action: "Release" },
    { from: StepStatus.GRANTEE_COMPLETE, to: StepStatus.PENDING, label: "Grantee Complete → Pending", action: "Submit" },
    { from: StepStatus.PENDING, to: StepStatus.APPROVED, label: "Pending → Approved", action: "Approve" },
    { from: StepStatus.PENDING_REVISION, to: StepStatus.GRANTEE_PENDING_REVISION, label: "Pending Revision → Grantee Pending Revision", action: "Request Revisions" },
];

// Step workflow subtask transitions - each maps to a subtask-level action
const stepWorkflowStepTransitions: Array<{ from: StepStatus; to: StepStatus; label: string; action: string }> = [
    { from: StepStatus.READY, to: StepStatus.GRANTEE_PENDING, label: "Ready → Grantee Pending", action: "Release" },
    { from: StepStatus.GRANTEE_COMPLETE, to: StepStatus.PENDING, label: "Grantee Complete → Pending", action: "Submit" },
    { from: StepStatus.PENDING_REVISION, to: StepStatus.GRANTEE_PENDING_REVISION, label: "Pending Revision → Grantee Pending Revision", action: "Request Revisions" },
    { from: StepStatus.PENDING, to: StepStatus.APPROVED, label: "Pending → Approved", action: "Approve" },
];

// None workflow transitions
const nonePhaseTransitions: Array<{ from: PhaseStatus; to: PhaseStatus; label: string; action: string }> = [
    { from: PhaseStatus.SETUP, to: PhaseStatus.APPROVED, label: "In Progress → Complete", action: "Mark Complete" },
];

const noneStepTransitions: Array<{ from: StepStatus; to: StepStatus; label: string; action: string }> = [
    { from: StepStatus.SETUP, to: StepStatus.APPROVED, label: "In Progress → Complete", action: "Mark Complete" },
];

// Available triggers for notification configuration. Same three at both levels.
//
// Task-level OnComment and OnUpload used to be left out of this UI on the belief that they
// always fired. They don't - a comment on a task sends nothing unless the task's OnComment
// config is enabled, and that config is what the DC tasks carry from seeding.
const taskTriggers: NotificationTrigger[] = [
    NotificationTrigger.OnStatusChange,
    NotificationTrigger.OnUpload,
    NotificationTrigger.OnComment,
];

const subtaskTriggers: NotificationTrigger[] = [
    NotificationTrigger.OnStatusChange,
    NotificationTrigger.OnUpload,
    NotificationTrigger.OnComment,
];

type TriggerState = {
    trigger: NotificationTrigger;
    enabled: boolean;
    notifyRoles: UserRole[];
    copyRoles: UserRole[];
    templateName: string | null;
    transitions: StatusTransitionConfig[];
};

// What the type's defaults filled in on one notification that the saved config lacks
type AddedDefault = {
    key: string;
    label: string;
    offNote: string;
    templateName: string | null;
    notifyRoles: UserRole[] | null;
};

const sameRoles = (a: UserRole[], b: UserRole[]) =>
    a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",");

const triggerKey = (trigger: NotificationTrigger) => `${trigger}`;

// Every notification card: checkbox | status and action | notify | copy, with the template
// spanning the last two in the header row
const cardStyle = (enabled: boolean): React.CSSProperties => ({
    display: "grid",
    gridTemplateColumns: "22px 280px minmax(0, 350px) minmax(0, 350px)",
    columnGap: "12px",
    rowGap: "10px",
    alignItems: "start",
    padding: "12px",
    backgroundColor: enabled ? "#f5f5f5" : "#fafafa",
    borderRadius: "4px",
    opacity: enabled ? 1 : 0.6,
});
const transitionKey = (trigger: NotificationTrigger, from: number, to: number) => `${trigger}:${from}-${to}`;

export default function TemplateNotificationsTab({ task, subtask, workflowType, configOptions, onSave, mode }: Props) {
    // Determine effective mode
    const effectiveMode: NotificationsMode = mode ?? (subtask ? "subtask" : "task");
    const isTaskMode = effectiveMode === "task";
    const isSubtaskMode = effectiveMode === "subtask";
    const isDefaultSubtaskMode = effectiveMode === "defaultSubtask";

    const roleOptions = configOptions.roles;
    const groupedRoles = useMemo(() => groupRoleOptions(roleOptions), [roleOptions]);
    const isPhaseLevel = isTaskMode;
    const isNoneWorkflow = workflowType === PhaseWorkflowType.None;
    const isStepWorkflow = workflowType === PhaseWorkflowType.Step;

    // Task level shows OnStatusChange only; subtask level shows all triggers
    const allTriggers = isTaskMode ? taskTriggers : subtaskTriggers;

    // Get transitions based on level and workflow type
    const getAvailableTransitions = () => {
        if (isPhaseLevel) {
            return isNoneWorkflow ? nonePhaseTransitions : phaseTransitions;
        }
        // Subtask level: use workflow-specific transitions
        if (isNoneWorkflow) return noneStepTransitions;
        if (isStepWorkflow) return stepWorkflowStepTransitions;
        return stepTransitions; // Phase workflow subtasks
    };

    const availableTransitions = getAvailableTransitions();

    // Get current notification config based on mode
    const getCurrentNotifications = (): NotificationRules | null => {
        if (isDefaultSubtaskMode) {
            return task?.settings?.defaultStepSettings?.notifications ?? null;
        }
        if (isSubtaskMode) {
            return subtask?.settings?.notifications ?? null;
        }
        return task?.settings?.notifications ?? null;
    };

    // The type's default notifications, the same ones Restore Defaults applies
    const getDefaultTriggerConfigs = () => (isTaskMode
        ? configOptions.task.defaultNotifications
        : configOptions.subtask.defaultNotifications)?.triggerConfigs ?? [];

    // Build trigger states from current config. With fillDefaults, an empty template or notify list
    // takes the type's default; the saved baseline is built without it, so a filled-in value shows
    // as an unsaved change until the user saves it. No default that is on notifies nobody, and the
    // registry holds the whole list as one setting, so an empty notify list means never set.
    const buildInitialState = (fillDefaults: boolean): TriggerState[] => {
        const currentNotifications = getCurrentNotifications();
        const triggerConfigs = currentNotifications?.triggerConfigs ?? [];
        const defaultTriggerConfigs = getDefaultTriggerConfigs();

        return allTriggers.map(trigger => {
            const existing = triggerConfigs.find(tc => tc.trigger === trigger);
            const defaultConfig = defaultTriggerConfigs.find(tc => tc.trigger === trigger);

            // Build transitions for OnStatusChange
            let transitions: StatusTransitionConfig[] = [];
            if (trigger === NotificationTrigger.OnStatusChange) {
                transitions = availableTransitions.map(t => {
                    const matches = (et: StatusTransitionConfig) => isPhaseLevel
                        ? et.fromPhaseStatus === t.from && et.toPhaseStatus === t.to
                        : et.fromStepStatus === t.from && et.toStepStatus === t.to;
                    const existingTransition = existing?.transitions?.find(matches);
                    const defaultTransition = defaultConfig?.transitions?.find(matches);

                    const base: StatusTransitionConfig = {
                        fromPhaseStatus: isPhaseLevel ? t.from as PhaseStatus : null,
                        toPhaseStatus: isPhaseLevel ? t.to as PhaseStatus : null,
                        fromStepStatus: isPhaseLevel ? null : t.from as StepStatus,
                        toStepStatus: isPhaseLevel ? null : t.to as StepStatus,
                        notifyRoles: [],
                        copyRoles: [],
                        templateName: null,
                        enabled: false,
                        subject: null,
                        attachStepFiles: false,
                        appDataAttachments: null,
                        attachGeneratedReport: false,
                        attachStepFilesForStatuses: null,
                        onlyApprovedSections: false,
                        showPendingApprovalWatermark: true,
                        showCompletionStamp: false,
                        showCoverPage: false,
                        includeRevisionRequestComment: false,
                    };

                    // Spread existing transition data to preserve all settings
                    const merged = existingTransition ? { ...base, ...existingTransition } : base;
                    return fillDefaults
                        ? {
                            ...merged,
                            templateName: merged.templateName || defaultTransition?.templateName || null,
                            notifyRoles: merged.notifyRoles?.length ? merged.notifyRoles : defaultTransition?.notifyRoles ?? [],
                        }
                        : merged;
                });
            }

            // Template name comes from saved config (seeded defaults or admin override)
            const templateName = fillDefaults
                ? existing?.templateName || defaultConfig?.templateName || null
                : existing?.templateName ?? null;

            // Status change roles are per transition; its trigger-level list isn't shown on this tab
            const savedRoles = existing?.notifyRoles ?? [];
            const notifyRoles = fillDefaults && !savedRoles.length && trigger !== NotificationTrigger.OnStatusChange
                ? defaultConfig?.notifyRoles ?? []
                : savedRoles;

            return {
                trigger,
                enabled: existing?.enabled ?? false,
                notifyRoles,
                copyRoles: existing?.copyRoles ?? [],
                templateName,
                transitions,
            };
        });
    };

    // Compares the filled-in states with the saved ones, so the notice can name what came in
    const findAddedDefaults = (filled: TriggerState[], saved: TriggerState[]): AddedDefault[] => {
        const added: AddedDefault[] = [];
        const addIfFilled = (key: string, label: string, offNote: string,
            filledName: string | null | undefined, savedName: string | null | undefined,
            filledRoles: UserRole[] | undefined, savedRoles: UserRole[] | undefined) => {
            const templateName = filledName && !savedName ? filledName : null;
            const notifyRoles = filledRoles?.length && !savedRoles?.length ? filledRoles : null;
            if (templateName || notifyRoles) added.push({ key, label, offNote, templateName, notifyRoles });
        };

        filled.forEach(t => {
            const savedTrigger = saved.find(st => st.trigger === t.trigger);
            // A trigger that is off hides its details, so say why they aren't on screen
            const offNote = t.enabled ? "" : ` (${getTriggerLabel(t.trigger)} is off, so it isn't shown below)`;
            // Status change templates and roles are per transition; its trigger-level ones aren't shown
            if (t.trigger !== NotificationTrigger.OnStatusChange) {
                addIfFilled(triggerKey(t.trigger), getTriggerLabel(t.trigger), offNote,
                    t.templateName, savedTrigger?.templateName, t.notifyRoles, savedTrigger?.notifyRoles);
            }
            availableTransitions.forEach(at => {
                const matches = (tr: StatusTransitionConfig) => isPhaseLevel
                    ? tr.fromPhaseStatus === at.from && tr.toPhaseStatus === at.to
                    : tr.fromStepStatus === at.from && tr.toStepStatus === at.to;
                const filledTransition = t.transitions.find(matches);
                const savedTransition = savedTrigger?.transitions.find(matches);
                addIfFilled(transitionKey(t.trigger, at.from, at.to), at.label, offNote,
                    filledTransition?.templateName, savedTransition?.templateName,
                    filledTransition?.notifyRoles, savedTransition?.notifyRoles);
            });
        });
        return added;
    };

    // One notice line per notification, naming each thing filled in
    const describeAddedDefault = (a: AddedDefault) => {
        const parts = [a.templateName && "email template", a.notifyRoles && "notify roles"].filter(Boolean);
        return `${a.label}: ${parts.join(" and ")}${a.offNote}`;
    };

    const [triggers, setTriggers] = useState<TriggerState[]>(() => buildInitialState(true));
    const [savedTriggers, setSavedTriggers] = useState<TriggerState[]>(() => buildInitialState(false));
    const [addedDefaults, setAddedDefaults] = useState<AddedDefault[]>(() => findAddedDefaults(buildInitialState(true), buildInitialState(false)));

    // A field keeps its tag only while it still holds the default that was filled in
    const isDefault = (key: string, templateName: string | null | undefined) =>
        addedDefaults.some(a => a.key === key && a.templateName != null && a.templateName === templateName);
    const isDefaultRoles = (key: string, roles: UserRole[] | undefined) =>
        addedDefaults.some(a => a.key === key && a.notifyRoles != null && sameRoles(a.notifyRoles, roles ?? []));

    // Email template viewer dialog state
    const [viewerVisible, setViewerVisible] = useState(false);
    const [viewerTemplateName, setViewerTemplateName] = useState<string | null>(null);
    const [viewerSpecialSettings, setViewerSpecialSettings] = useState<StatusTransitionConfig | null>(null);
    const [viewerContextId, setViewerContextId] = useState<number | undefined>(undefined);
    const [viewerContextType, setViewerContextType] = useState<"task" | "subtask" | undefined>(undefined);
    // Context passed to backend on reset for proper default lookup
    const [viewerResetContext, setViewerResetContext] = useState<Partial<ResetEmailTemplateDto> | undefined>(undefined);
    // Track which transition the viewer was opened for (null = trigger-level template)
    const [viewerTransitionKey, setViewerTransitionKey] = useState<{ trigger: NotificationTrigger; from?: number; to?: number } | null>(null);

    // Reset state when task/subtask changes
    useEffect(() => {
        const filled = buildInitialState(true);
        const saved = buildInitialState(false);
        setTriggers(filled);
        setSavedTriggers(saved);
        setAddedDefaults(findAddedDefaults(filled, saved));
    }, [task?.id, subtask?.id, effectiveMode, workflowType]);

    // Check if triggers are dirty (different from saved state)
    const isDirty = useMemo(() => {
        const toComparable = (triggerList: TriggerState[]) => {
            return triggerList.map(t => {
                const transitionsStr = t.transitions
                    .map(tr => `${tr.fromPhaseStatus ?? tr.fromStepStatus}-${tr.toPhaseStatus ?? tr.toStepStatus}-${tr.enabled}-${tr.templateName ?? ""}-${(tr.notifyRoles ?? []).sort().join(",")}-${(tr.copyRoles ?? []).sort().join(",")}`)
                    .sort()
                    .join("|");
                return `${t.trigger}-${t.enabled}-${t.templateName ?? ""}-${(t.notifyRoles ?? []).sort().join(",")}-${(t.copyRoles ?? []).sort().join(",")}-[${transitionsStr}]`;
            }).sort().join(";");
        };
        return toComparable(triggers) !== toComparable(savedTriggers);
    }, [triggers, savedTriggers]);

    const updateTrigger = (trigger: NotificationTrigger, updates: Partial<TriggerState>) => {
        setTriggers(prev =>
            prev.map(t => {
                if (t.trigger !== trigger) return t;

                const newState = { ...t, ...updates };

                return newState;
            })
        );
    };

    const updateTransition = (
        trigger: NotificationTrigger,
        from: PhaseStatus | StepStatus,
        to: PhaseStatus | StepStatus,
        updates: Partial<StatusTransitionConfig>
    ) => {
        setTriggers(prev =>
            prev.map(t => {
                if (t.trigger !== trigger) return t;

                const newTransitions = t.transitions.map(tr => {
                    const matches = isPhaseLevel
                        ? tr.fromPhaseStatus === from && tr.toPhaseStatus === to
                        : tr.fromStepStatus === from && tr.toStepStatus === to;

                    if (!matches) return tr;
                    return { ...tr, ...updates };
                });

                return { ...t, transitions: newTransitions };
            })
        );
    };

    // Determine context for template customization
    const getViewerContext = (): { id: number | undefined; type: "task" | "subtask" | undefined } => {
        if (isDefaultSubtaskMode || isTaskMode) {
            return { id: task?.id ?? undefined, type: "task" };
        }
        return { id: subtask?.id ?? undefined, type: "subtask" };
    };

    const openTemplateViewer = (
        tplName: string | null,
        settings: StatusTransitionConfig | null,
        transitionKey: { trigger: NotificationTrigger; from?: number; to?: number } | null,
    ) => {
        const ctx = getViewerContext();
        setViewerTemplateName(tplName);
        setViewerSpecialSettings(settings);
        setViewerContextId(ctx.id);
        setViewerContextType(ctx.type);
        setViewerTransitionKey(transitionKey);

        // Build reset context for the backend (subtask mode only — phase-level falls back to core)
        if (isSubtaskMode && transitionKey) {
            setViewerResetContext({
                defaultPhaseId: subtask?.phaseTypeId ?? null,
                defaultStepId: subtask?.id ?? null,
                trigger: transitionKey.trigger,
                fromStepStatus: transitionKey.from ?? null,
                toStepStatus: transitionKey.to ?? null,
            });
        } else {
            setViewerResetContext(undefined);
        }

        setViewerVisible(true);
    };

    // Apply a template name change to triggers and return the updated list
    const applyTemplateNameChange = (templateName: string): TriggerState[] => {
        if (!viewerTransitionKey) return triggers;
        const { trigger, from, to } = viewerTransitionKey;
        return triggers.map(t => {
            if (t.trigger !== trigger) return t;
            if (from !== undefined && to !== undefined) {
                const newTransitions = t.transitions.map(tr => {
                    const matches = isPhaseLevel
                        ? tr.fromPhaseStatus === from && tr.toPhaseStatus === to
                        : tr.fromStepStatus === from && tr.toStepStatus === to;
                    return matches ? { ...tr, templateName } : tr;
                });
                return { ...t, transitions: newTransitions };
            }
            return { ...t, templateName };
        });
    };

    const handleTemplateCustomized = async (newTemplateName: string) => {
        const updated = applyTemplateNameChange(newTemplateName);
        setTriggers(updated);
        await saveTriggersToBackend(updated);
    };

    const handleTemplateReset = async (coreTemplateName: string) => {
        const updated = applyTemplateNameChange(coreTemplateName);
        setTriggers(updated);
        await saveTriggersToBackend(updated);
    };

    const handleSettingsChanged = async (updates: Partial<StatusTransitionConfig>) => {
        if (!viewerTransitionKey) return;
        const { trigger, from, to } = viewerTransitionKey;
        if (from === undefined || to === undefined) return;
        // Update the transition in triggers state
        const updated = triggers.map(t => {
            if (t.trigger !== trigger) return t;
            const newTransitions = t.transitions.map(tr => {
                const matches = isPhaseLevel
                    ? tr.fromPhaseStatus === from && tr.toPhaseStatus === to
                    : tr.fromStepStatus === from && tr.toStepStatus === to;
                return matches ? { ...tr, ...updates } : tr;
            });
            return { ...t, transitions: newTransitions };
        });
        setTriggers(updated);
        // Also update the viewerSpecialSettings so the checkboxes reflect the change immediately
        if (viewerSpecialSettings) {
            setViewerSpecialSettings({ ...viewerSpecialSettings, ...updates });
        }
        await saveTriggersToBackend(updated);
    };

    const saveTriggersToBackend = async (triggerList: TriggerState[]) => {
        const edited: NotificationTriggerConfig[] = triggerList.map(t => ({
            trigger: t.trigger,
            enabled: t.enabled,
            notifyRoles: t.notifyRoles,
            copyRoles: t.copyRoles,
            templateName: t.templateName,
            transitions: t.trigger === NotificationTrigger.OnStatusChange
                ? t.transitions
                : null,
            subject: null,
        }));

        // The save replaces the whole rules object on the backend, so anything this tab doesn't
        // render has to be carried across or it is deleted - and once deleted the settings
        // registry treats it as a user edit, so re-seeding won't bring it back.
        const editedTriggers = new Set(edited.map(t => t.trigger));
        const untouched = (getCurrentNotifications()?.triggerConfigs ?? [])
            .filter(tc => !editedTriggers.has(tc.trigger));

        const notificationRules: NotificationRules = {
            triggerConfigs: [...edited, ...untouched],
        };

        if (isDefaultSubtaskMode && task) {
            const currentDefaults = task.settings?.defaultStepSettings;
            const updatedTask: DefaultPhaseDto = {
                ...task,
                settings: {
                    ...task.settings,
                    defaultStepSettings: {
                        unrestrictedUpload: currentDefaults?.unrestrictedUpload ?? false,
                        fileRestrictions: currentDefaults?.fileRestrictions ?? null,
                        fileLimit: currentDefaults?.fileLimit ?? null,
                        allowedExtensions: currentDefaults?.allowedExtensions ?? null,
                        offsetStart: currentDefaults?.offsetStart ?? null,
                        offsetEnd: currentDefaults?.offsetEnd ?? null,
                        dueDate: currentDefaults?.dueDate ?? null,
                        requestFileAttachments: currentDefaults?.requestFileAttachments ?? null,
                        triggerActions: currentDefaults?.triggerActions ?? null,
                        initialStepStatus: currentDefaults?.initialStepStatus ?? null,
                        notifications: notificationRules,
                        workflow: currentDefaults?.workflow ?? null,
                        workflowType: currentDefaults?.workflowType ?? null,
                        isCore: currentDefaults?.isCore ?? false,
                    }
                }
            };
            await onSave(updatedTask);
        } else if (isSubtaskMode && subtask) {
            const updatedSubtask: DefaultStepDto = {
                ...subtask,
                settings: {
                    ...subtask.settings,
                    notifications: notificationRules,
                }
            };
            await onSave(updatedSubtask);
        } else if (isTaskMode && task) {
            const updatedTask: DefaultPhaseDto = {
                ...task,
                settings: {
                    ...task.settings,
                    notifications: notificationRules,
                }
            };
            await onSave(updatedTask);
        }

        setSavedTriggers([...triggerList]);
        setAddedDefaults([]);
    };

    const handleSave = async () => {
        await saveTriggersToBackend(triggers);
    };

    const handleClearAll = () => {
        setAddedDefaults([]);
        setTriggers(prev => prev.map(t => ({
            ...t,
            enabled: false,
            notifyRoles: [],
            copyRoles: [],
            templateName: null,
            transitions: t.transitions.map(tr => ({
                ...tr,
                notifyRoles: [],
                copyRoles: [],
                templateName: null,
                enabled: false,
            })),
        })));
    };

    const handleRestoreDefaults = () => {
        // Get default notifications based on mode
        const defaultNotifications = isTaskMode
            ? configOptions.task.defaultNotifications
            : configOptions.subtask.defaultNotifications;

        const defaultTriggerConfigs = defaultNotifications?.triggerConfigs ?? [];

        // Rebuild trigger states from defaults
        const restoredTriggers = allTriggers.map(trigger => {
            const defaultConfig = defaultTriggerConfigs.find(tc => tc.trigger === trigger);

            // Build transitions for OnStatusChange
            let transitions: StatusTransitionConfig[] = [];
            if (trigger === NotificationTrigger.OnStatusChange) {
                transitions = availableTransitions.map(t => {
                    const defaultTransition = defaultConfig?.transitions?.find(et => {
                        if (isPhaseLevel) {
                            return et.fromPhaseStatus === t.from && et.toPhaseStatus === t.to;
                        }
                        return et.fromStepStatus === t.from && et.toStepStatus === t.to;
                    });

                    const base: StatusTransitionConfig = {
                        fromPhaseStatus: isPhaseLevel ? t.from as PhaseStatus : null,
                        toPhaseStatus: isPhaseLevel ? t.to as PhaseStatus : null,
                        fromStepStatus: isPhaseLevel ? null : t.from as StepStatus,
                        toStepStatus: isPhaseLevel ? null : t.to as StepStatus,
                        notifyRoles: [],
                        copyRoles: [],
                        templateName: null,
                        enabled: false,
                        subject: null,
                        attachStepFiles: false,
                        appDataAttachments: null,
                        attachGeneratedReport: false,
                        attachStepFilesForStatuses: null,
                        onlyApprovedSections: false,
                        showPendingApprovalWatermark: true,
                        showCompletionStamp: false,
                        showCoverPage: false,
                        includeRevisionRequestComment: false,
                    };

                    return defaultTransition
                        ? { ...base, ...defaultTransition }
                        : base;
                });
            }

            // Template name comes from the seeded default config. UI doesn't invent one.
            const isEnabled = defaultConfig?.enabled ?? false;
            const templateName = defaultConfig?.templateName ?? null;

            return {
                trigger,
                enabled: isEnabled,
                notifyRoles: defaultConfig?.notifyRoles ?? [],
                copyRoles: defaultConfig?.copyRoles ?? [],
                templateName,
                transitions,
            };
        });

        setTriggers(restoredTriggers);
        setAddedDefaults([]);
    };

    const descriptionText = isDefaultSubtaskMode
        ? "Configure default notification settings for new subtasks."
        : isSubtaskMode
            ? "Configure which notifications are sent for this subtask."
            : "Configure which notifications are sent for this task.";

    return (
        <div className="template-tab">
            <div className="template-tab-body">
            <p style={{ marginBottom: "16px", color: "#666" }}>
                {descriptionText}
            </p>

            <DefaultsAddedNotice items={addedDefaults.map(describeAddedDefault)} />

            <div style={{ display: "grid", gap: "24px" }}>
                {triggers.map(triggerState => (
                    <div
                        key={triggerState.trigger}
                        style={{
                            padding: "16px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            backgroundColor: triggerState.enabled ? "#fff" : "#f9f9f9",
                        }}
                    >
                        {/* Trigger header */}
                        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
                            <CheckBox
                                value={triggerState.enabled}
                                onValueChanged={(e) => updateTrigger(triggerState.trigger, { enabled: e.value })}
                            />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: "14px" }}>
                                    {getTriggerLabel(triggerState.trigger)}
                                </div>
                                <div style={{ fontSize: "12px", color: "#888" }}>
                                    {getTriggerDescription(triggerState.trigger)}
                                </div>
                            </div>
                        </div>

                        {/* Trigger details - only show when enabled */}
                        {triggerState.enabled && (
                            <div style={{ marginLeft: "32px" }}>
                                {/* For OnStatusChange, show transition-specific config */}
                                {triggerState.trigger === NotificationTrigger.OnStatusChange ? (
                                    <div style={{ display: "grid", gap: "12px" }}>
                                        <div style={{ fontSize: "13px", fontWeight: 500, marginBottom: "4px" }}>
                                            Configure notifications for each status transition:
                                        </div>
                                        {availableTransitions.map(t => {
                                            const transition = triggerState.transitions.find(tr =>
                                                isPhaseLevel
                                                    ? tr.fromPhaseStatus === t.from && tr.toPhaseStatus === t.to
                                                    : tr.fromStepStatus === t.from && tr.toStepStatus === t.to
                                            );
                                            const isTransitionEnabled = transition?.enabled ?? false;

                                            return (
                                                <div
                                                    key={`${t.from}-${t.to}`}
                                                    style={cardStyle(isTransitionEnabled)}
                                                >
                                                    <CheckBox
                                                        value={isTransitionEnabled}
                                                        onValueChanged={(e) =>
                                                            updateTransition(
                                                                triggerState.trigger,
                                                                t.from,
                                                                t.to,
                                                                { enabled: e.value }
                                                            )
                                                        }
                                                    />
                                                    <div>
                                                        <div style={{ fontWeight: 500, fontSize: "13px" }}>{t.label}</div>
                                                        <div style={{ fontSize: "11px", color: "#666" }}>Action: {t.action}</div>
                                                    </div>
                                                    <div style={{ gridColumn: "3 / 5", display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: "6px" }}>
                                                        <span style={{ fontSize: "11px", color: "#666" }}>Template:</span>
                                                        {transition?.templateName && (
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    openTemplateViewer(
                                                                        transition.templateName,
                                                                        transition,
                                                                        { trigger: triggerState.trigger, from: t.from, to: t.to },
                                                                    );
                                                                }}
                                                                style={{ fontSize: "12px", color: "#337ab7", textDecoration: "underline", cursor: "pointer" }}
                                                            >
                                                                {transition.templateName}
                                                            </a>
                                                        )}
                                                        {isDefault(transitionKey(triggerState.trigger, t.from, t.to), transition?.templateName) && <DefaultTag />}
                                                    </div>
                                                    <div style={{ gridColumn: 3 }}>
                                                        <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                                                            Notify these roles:
                                                            {isDefaultRoles(transitionKey(triggerState.trigger, t.from, t.to), transition?.notifyRoles) && <DefaultTag />}
                                                        </div>
                                                        <TagBox
                                                            items={groupedRoles}
                                                            displayExpr="name"
                                                            valueExpr="id"
                                                            grouped={true}
                                                            value={transition?.notifyRoles ?? []}
                                                            onValueChanged={(e) =>
                                                                updateTransition(
                                                                    triggerState.trigger,
                                                                    t.from,
                                                                    t.to,
                                                                    { notifyRoles: e.value ?? [] }
                                                                )
                                                            }
                                                            width="100%"
                                                            placeholder="Select roles to notify..."
                                                            showSelectionControls={true}
                                                            disabled={!isTransitionEnabled}
                                                        />
                                                    </div>
                                                    <div style={{ gridColumn: 4 }}>
                                                        <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                                                            Copy these roles:
                                                        </div>
                                                        <TagBox
                                                            items={groupedRoles}
                                                            displayExpr="name"
                                                            valueExpr="id"
                                                            grouped={true}
                                                            value={transition?.copyRoles ?? []}
                                                            onValueChanged={(e) =>
                                                                updateTransition(
                                                                    triggerState.trigger,
                                                                    t.from,
                                                                    t.to,
                                                                    { copyRoles: e.value ?? [] }
                                                                )
                                                            }
                                                            width="100%"
                                                            placeholder="Select roles to copy..."
                                                            showSelectionControls={true}
                                                            disabled={!isTransitionEnabled}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    /* OnUpload and OnComment have one set of roles, in the same card as a transition */
                                    <div style={cardStyle(true)}>
                                        <div style={{ gridColumn: "3 / 5", display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: "6px" }}>
                                            <span style={{ fontSize: "11px", color: "#666" }}>Template:</span>
                                            {triggerState.templateName && (
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        openTemplateViewer(
                                                            triggerState.templateName,
                                                            null,
                                                            { trigger: triggerState.trigger },
                                                        );
                                                    }}
                                                    style={{ fontSize: "12px", color: "#337ab7", textDecoration: "underline", cursor: "pointer" }}
                                                >
                                                    {triggerState.templateName}
                                                </a>
                                            )}
                                            {isDefault(triggerKey(triggerState.trigger), triggerState.templateName) && <DefaultTag />}
                                        </div>
                                        <div style={{ gridColumn: 3 }}>
                                            <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                                                Notify these roles:
                                                {isDefaultRoles(triggerKey(triggerState.trigger), triggerState.notifyRoles) && <DefaultTag />}
                                            </div>
                                            <TagBox
                                                items={groupedRoles}
                                                displayExpr="name"
                                                valueExpr="id"
                                                grouped={true}
                                                value={triggerState.notifyRoles}
                                                onValueChanged={(e) =>
                                                    updateTrigger(triggerState.trigger, { notifyRoles: e.value ?? [] })
                                                }
                                                width="100%"
                                                placeholder="Select roles to notify..."
                                                showSelectionControls={true}
                                            />
                                        </div>
                                        <div style={{ gridColumn: 4 }}>
                                            <div style={{ fontSize: "11px", color: "#666", marginBottom: "4px" }}>
                                                Copy these roles:
                                            </div>
                                            <TagBox
                                                items={groupedRoles}
                                                displayExpr="name"
                                                valueExpr="id"
                                                grouped={true}
                                                value={triggerState.copyRoles}
                                                onValueChanged={(e) =>
                                                    updateTrigger(triggerState.trigger, { copyRoles: e.value ?? [] })
                                                }
                                                width="100%"
                                                placeholder="Select roles to copy..."
                                                showSelectionControls={true}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save Notifications *" : "Save Notifications"}
                    type="default"
                    stylingMode="contained"
                    onClick={handleSave}
                />
                {isDirty && (
                    <Button
                        text="Discard Changes"
                        type="normal"
                        stylingMode="outlined"
                        onClick={() => {
                            setTriggers([...savedTriggers]);
                            setAddedDefaults([]);
                        }}
                    />
                )}
                <Button
                    text="Restore Defaults"
                    type="normal"
                    stylingMode="outlined"
                    onClick={handleRestoreDefaults}
                />
                <Button
                    text="Clear All"
                    type="danger"
                    stylingMode="outlined"
                    onClick={handleClearAll}
                />
                {isDirty && (
                    <span style={{ color: "#f0ad4e", fontSize: "13px", marginLeft: "8px" }}>
                        Unsaved changes
                    </span>
                )}
            </div>

            <EmailTemplateViewerDialog
                visible={viewerVisible}
                onHiding={() => setViewerVisible(false)}
                templateName={viewerTemplateName}
                specialSettings={viewerSpecialSettings}
                contextId={viewerContextId}
                contextType={viewerContextType}
                phaseTypeId={task?.id ?? subtask?.phaseTypeId}
                resetContext={viewerResetContext}
                onTemplateCustomized={handleTemplateCustomized}
                onTemplateReset={handleTemplateReset}
                onSettingsChanged={handleSettingsChanged}
            />
        </div>
    );
}
